import { Store } from '@reduxjs/toolkit';
import { Exception } from '@sentry/browser/build/npm/types';

import { config } from '@/config';
import { reportErrorToSentry } from '@/configuration/setup/sentry';
import { trace } from '@/configuration/setup/trace';
import { DEFAULT_LOCALE, getSupportedLocale as defaultGetSupportedLocale } from './lang';
import { displayMessagesFetched, localeChanged } from './langSlice';

const sendError = (exception: Exception) => {
    reportErrorToSentry(exception, {
        tags: {
            module: config.sentryModuleName,
        },
    });
};

const translationModules = import.meta.glob('/src/features/translations/*.json');

const importDisplayMessages = async (locale: string): Promise<Record<string, string>> => {
    const path = `/src/features/translations/${locale}.json`;
    const loader = translationModules[path];
    const module = await loader();
    return (module as { default: Record<string, string> }).default;
};

export const configureFetchDisplayMessages =
    (store: Store, fetchDisplayMessages = importDisplayMessages, getSupportedLocale = defaultGetSupportedLocale) =>
    async (locale: string) => {
        if (!locale) {
            console.warn('No "locale" supplied when fetching display messages!');
            return Promise.reject();
        }

        const supportedLocale = getSupportedLocale(locale);

        try {
            const displayMessages = await fetchDisplayMessages(supportedLocale);
            trace(`Display messages fetched for "${supportedLocale}"`);
            store.dispatch(displayMessagesFetched({ locale: supportedLocale, displayMessages }));
        } catch (error) {
            console.error(`Display messages for "${supportedLocale}" could not be fetched.`, error);
            sendError(error as Exception);
            store.dispatch(localeChanged(DEFAULT_LOCALE));
        }
    };
